import {
    DEFAULT_ATS_SECTION_TITLES,
    createCustomSection,
    createEducationEntry,
    createExperienceEntry,
    createProjectEntry,
    displayField,
} from "./atsCvFormEngine.js";

const EMAIL_RE = /[\w.+-]+@[\w.-]+\.\w+/;
const PHONE_RE = /(?:\+?\d[\d\s().-]{7,}\d)/;
const LINKEDIN_RE = /(?:linkedin\.com\/\S+|linkedin:\s*\S+)/i;
const GITHUB_RE = /github\.com\/\S+/i;
const URL_RE = /(?:https?:\/\/|www\.)\S+/i;
const YEAR_ONLY_RE = /^\d{4}$/;
const DATE_RANGE_RE =
    /((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s*)?(\d{4})\s*[-–—]\s*(Present|Current|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s*\d{4}|\d{4})/i;

const SECTION_RULES = [
    { key: "summary", match: /^(professional\s+)?summary$|^profile$|^objective$|^about(\s+me)?$/i },
    {
        key: "experience",
        match: /^(professional\s+)?experience$|^work\s+experience$|^employment(\s+history)?$|^career$/i,
    },
    { key: "education", match: /^education$|^academic(\s+background)?$|^qualifications?$/i },
    { key: "projects", match: /^projects?$|^personal\s+projects?$/i },
    { key: "skills", match: /^skills?$|^technical\s+skills?$|^core\s+competencies$/i },
    {
        key: "certifications",
        match: /^certifications?$|^certificates?$|^licenses?$/i,
    },
    { key: "languages", match: /^languages?$|^language\s+skills?$/i },
];

const KNOWN_SECTION_KEYS = new Set(SECTION_RULES.map((r) => r.key));

/**
 * Clean raw PDF/OCR lines before parsing.
 * @param {string[]} lines
 */
export function normalizeExtractedCvLines(lines) {
    const out = [];

    for (const raw of lines) {
        const line = String(raw ?? "").trim();
        if (!line) continue;

        if (/^পৃষ্ঠা\s*\d/i.test(line)) continue;
        if (/^page\s*\d/i.test(line)) continue;
        if (/^\d+\s*\/\s*\d+$/.test(line)) continue;
        if (/^sohozkaj/i.test(line)) continue;
        if (line.includes("@@SOHOZKAJ_ATS_CV")) continue;
        if (/^[A-Za-z0-9+/]{40,}={0,2}$/.test(line)) continue;

        const lower = line.toLowerCase();
        if (out.length > 0 && out[out.length - 1].toLowerCase() === lower) continue;

        const prev = out[out.length - 1];
        if (prev && isSectionHeader(prev) && isSectionHeader(line)) {
            const a = isSectionHeader(prev);
            const b = isSectionHeader(line);
            if (a?.key === b?.key) continue;
        }

        out.push(line);
    }

    return out;
}

function isBulletLine(line) {
    return (
        /^[\s•·▪◦‣⁃\-*–—]\s*\S/.test(line) ||
        /^\d+[.)]\s+\S/.test(line) ||
        /^[\u2022\u00B7\u2219]\s*\S/.test(line)
    );
}

function stripBullet(line) {
    return String(line)
        .replace(/^[\s•·▪◦‣⁃\-*–—]+/, "")
        .replace(/^\d+[.)]\s+/, "")
        .trim();
}

function isSectionHeader(line) {
    const t = line.trim();
    if (!t || t.length > 60) return null;

    const norm = t
        .replace(/[^a-zA-Z\s&/]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    for (const rule of SECTION_RULES) {
        if (rule.match.test(norm) || rule.match.test(t)) {
            return { key: rule.key, title: t.toUpperCase() };
        }
    }

    if (/^[A-Z][A-Z0-9\s&/,-]{2,48}$/.test(t) && t.length <= 42 && !EMAIL_RE.test(t)) {
        return { key: "custom", title: t.toUpperCase() };
    }
    return null;
}

function parseDateRange(text) {
    const m = text.match(DATE_RANGE_RE);
    if (!m) return { startDate: "", endDate: "", isCurrent: false };
    const parts = m[0].split(/\s*[-–—]\s*/i);
    const start = (parts[0] || "").trim();
    const endRaw = (parts[1] || "").trim();
    const isCurrent = /present|current/i.test(endRaw);
    return {
        startDate: start,
        endDate: isCurrent ? "" : endRaw,
        isCurrent,
    };
}

function splitTabLine(line) {
    if (line.includes("\t")) {
        return line.split(/\t+/).map((s) => s.trim()).filter(Boolean);
    }

    const dateMatch = line.match(DATE_RANGE_RE);
    if (dateMatch) {
        const idx = line.indexOf(dateMatch[0]);
        if (idx > 0) {
            const left = line.slice(0, idx).trim();
            const right = line.slice(idx).trim();
            if (left) return [left, right];
        }
    }

    if (YEAR_ONLY_RE.test(line.trim())) {
        return [line.trim()];
    }

    const multi = line.match(/^(.+?)\s{2,}(.+)$/);
    if (multi) return [multi[1].trim(), multi[2].trim()];
    return [line.trim()];
}

function assignProjectMeta(entry, token) {
    if (!token) return;
    if (YEAR_ONLY_RE.test(token) || DATE_RANGE_RE.test(token)) {
        entry.date = token.match(DATE_RANGE_RE)?.[0] || token;
    } else if (GITHUB_RE.test(token) || URL_RE.test(token) || LINKEDIN_RE.test(token)) {
        entry.link = token;
    } else if (!entry.technologies) {
        entry.technologies = token;
    } else {
        entry.link = entry.link || token;
    }
}

function isExperienceTitleRow(line) {
    if (!line || isSectionHeader(line)) return false;
    const parts = splitTabLine(line);
    return Boolean(
        line.match(DATE_RANGE_RE) ||
            parts.some((p) => DATE_RANGE_RE.test(p) || YEAR_ONLY_RE.test(p))
    );
}

function isProjectTitleRow(line) {
    if (!line || isSectionHeader(line)) return false;
    const parts = splitTabLine(line);
    return (
        parts.length >= 2 &&
        (YEAR_ONLY_RE.test(parts[parts.length - 1]) || DATE_RANGE_RE.test(parts[parts.length - 1]))
    );
}

function isEducationTitleRow(line) {
    if (!line || isSectionHeader(line)) return false;
    const parts = splitTabLine(line);
    if (parts.length >= 2) {
        const right = parts[parts.length - 1];
        return YEAR_ONLY_RE.test(right) || DATE_RANGE_RE.test(right);
    }
    return false;
}

/**
 * Collect bullet / description lines until the next entry or section.
 * @param {string[]} lines
 * @param {number} startIndex
 * @param {"experience"|"education"|"project"} kind
 */
function collectDescriptionLines(lines, startIndex, kind) {
    const bullets = [];
    let i = startIndex;

    while (i < lines.length) {
        const line = lines[i];
        if (!line || isSectionHeader(line)) break;
        if (kind === "experience" && isExperienceTitleRow(line)) break;
        if (kind === "project" && isProjectTitleRow(line)) break;
        if (kind === "education" && isEducationTitleRow(line)) break;

        if (isBulletLine(line)) {
            bullets.push(stripBullet(line));
        } else {
            bullets.push(line.trim());
        }
        i++;
    }

    return { bullets, nextIndex: i };
}

/**
 * @param {string[]} lines
 * @param {() => object} factory
 * @param {"experience"|"education"|"project"} kind
 */
function parseEntrySection(lines, factory, kind) {
    const entries = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        if (!line) {
            i++;
            continue;
        }

        if (isSectionHeader(line)) {
            break;
        }

        const entry = factory();
        const parts = splitTabLine(line);
        const dateOnLine = line.match(DATE_RANGE_RE);

        if (kind === "experience") {
            if (parts.length >= 2 && dateOnLine) {
                entry.jobTitle = parts[0];
                Object.assign(entry, parseDateRange(parts[parts.length - 1]));
                i++;
                if (i < lines.length && !isBulletLine(lines[i]) && !isSectionHeader(lines[i])) {
                    const sub = splitTabLine(lines[i]);
                    entry.company = sub[0] || "";
                    entry.location = sub[1] || "";
                    i++;
                }
            } else if (dateOnLine) {
                const idx = dateOnLine.index ?? 0;
                entry.jobTitle = line.slice(0, idx).trim() || parts[0] || "";
                Object.assign(entry, parseDateRange(dateOnLine[0]));
                i++;
                if (i < lines.length && !isBulletLine(lines[i]) && !isSectionHeader(lines[i])) {
                    const sub = splitTabLine(lines[i]);
                    entry.company = sub[0] || lines[i];
                    entry.location = sub[1] || "";
                    i++;
                }
            } else {
                entry.jobTitle = parts[0] || line;
                i++;
                if (i < lines.length && !isBulletLine(lines[i]) && !isSectionHeader(lines[i])) {
                    const sub = splitTabLine(lines[i]);
                    entry.company = sub[0] || "";
                    entry.location = sub[1] || "";
                    i++;
                }
            }
        } else if (kind === "education") {
            if (parts.length >= 2) {
                entry.degree = parts[0];
                const right = parts[parts.length - 1];
                if (DATE_RANGE_RE.test(right) || YEAR_ONLY_RE.test(right)) {
                    entry.graduationYear = right.match(DATE_RANGE_RE)?.[0] || right;
                    if (parts.length >= 3) entry.institution = parts[1];
                } else {
                    entry.institution = parts[1];
                }
                i++;
                if (i < lines.length && !isBulletLine(lines[i]) && !isSectionHeader(lines[i])) {
                    const sub = splitTabLine(lines[i]);
                    if (!entry.institution) entry.institution = sub[0] || "";
                    entry.location = sub[1] || entry.location || "";
                    i++;
                }
            } else {
                entry.degree = line;
                i++;
                if (i < lines.length && !isBulletLine(lines[i]) && !isSectionHeader(lines[i])) {
                    entry.institution = lines[i];
                    i++;
                }
            }
        } else if (kind === "project") {
            entry.name = parts[0] || line;
            parts.slice(1).forEach((p) => assignProjectMeta(entry, p));
            i++;
            while (
                i < lines.length &&
                !isBulletLine(lines[i]) &&
                !isSectionHeader(lines[i]) &&
                !isProjectTitleRow(lines[i]) &&
                splitTabLine(lines[i]).length === 1 &&
                !DATE_RANGE_RE.test(lines[i])
            ) {
                assignProjectMeta(entry, lines[i]);
                i++;
            }
        }

        const collected =
            kind === "experience" || kind === "project" || kind === "education"
                ? collectDescriptionLines(lines, i, kind)
                : { bullets: [], nextIndex: i };
        const bullets = collected.bullets;
        i = collected.nextIndex;

        if (kind === "experience" || kind === "project") {
            entry.description = bullets.join("\n");
        } else {
            entry.details = bullets.join("\n");
        }

        const hasContent =
            kind === "experience"
                ? entry.jobTitle || entry.company
                : kind === "education"
                  ? entry.degree || entry.institution
                  : entry.name;

        if (hasContent) entries.push(entry);
    }

    return entries;
}

function extractContactsFromLines(lines) {
    const joined = lines.join(" ");
    const email = joined.match(EMAIL_RE)?.[0] || "";
    const phone = joined.match(PHONE_RE)?.[0]?.trim() || "";
    const linkedin = joined.match(LINKEDIN_RE)?.[0]?.replace(/^linkedin:\s*/i, "") || "";
    let website = joined.match(GITHUB_RE)?.[0] || joined.match(URL_RE)?.[0] || "";
    if (website && linkedin && website.toLowerCase().includes("linkedin")) {
        website = joined.match(GITHUB_RE)?.[0] || "";
    }

    let location = "";
    for (const line of lines.slice(0, 12)) {
        if (EMAIL_RE.test(line) || PHONE_RE.test(line) || LINKEDIN_RE.test(line)) {
            const bits = line
                .split(/[|•·]/)
                .map((s) => s.trim())
                .filter(
                    (s) =>
                        s &&
                        !EMAIL_RE.test(s) &&
                        !PHONE_RE.test(s) &&
                        !LINKEDIN_RE.test(s) &&
                        !GITHUB_RE.test(s) &&
                        !URL_RE.test(s)
                );
            if (bits[0] && bits[0].length < 80) {
                location = bits[0];
                break;
            }
        }
    }

    return { email, phone, linkedin, website, location };
}

function parseHeaderLines(lines, firstSectionIndex) {
    const contacts = extractContactsFromLines(lines);
    const headerEnd = firstSectionIndex > 0 ? firstSectionIndex : Math.min(lines.length, 12);
    const headerLines = lines.slice(0, headerEnd);

    let fullName = "";
    let jobTitle = "";

    const firstSectionAt = headerLines.findIndex((l) => isSectionHeader(l));
    const preSection =
        firstSectionAt > 0 ? headerLines.slice(0, firstSectionAt) : headerLines;

    const contactIdx = preSection.findIndex(
        (l) =>
            EMAIL_RE.test(l) ||
            PHONE_RE.test(l) ||
            LINKEDIN_RE.test(l) ||
            GITHUB_RE.test(l) ||
            (l.includes("|") && (EMAIL_RE.test(l) || PHONE_RE.test(l)))
    );

    if (contactIdx > 0) {
        fullName = preSection[0] || "";
        jobTitle = preSection.slice(1, contactIdx).join(" ").trim();
    } else if (preSection.length >= 2) {
        fullName = preSection[0] || "";
        jobTitle = preSection[1] || "";
    } else if (preSection.length === 1) {
        fullName = preSection[0] || "";
    }

    if (EMAIL_RE.test(fullName) || PHONE_RE.test(fullName)) fullName = "";
    if (EMAIL_RE.test(jobTitle) || PHONE_RE.test(jobTitle)) jobTitle = "";

    fullName = fullName.replace(/[|•·].*$/, "").trim();
    jobTitle = jobTitle.replace(/[|•·].*$/, "").trim();

    return { fullName, jobTitle, ...contacts };
}

function listSectionToText(lines) {
    return lines
        .map((l) => (isBulletLine(l) ? stripBullet(l) : l.trim()))
        .filter((line) => line && !isSectionHeader(line))
        .join("\n");
}

function reindexEntries(entries, prefix, factory) {
    return entries.map((entry, i) => ({
        ...factory(`${prefix}-${i}`),
        ...entry,
        id: `${prefix}-${i}`,
    }));
}

/**
 * @param {string[]} lines
 */
export function parseAtsCvFromLines(rawLines) {
    const warnings = [];
    const cleaned = normalizeExtractedCvLines(rawLines);

    if (!cleaned.length) {
        return { warnings: ["PDF থেকে কোনো টেক্সট পাওয়া যায়নি। স্ক্যান করা PDF হলে OCR দরকার হতে পারে।"] };
    }

    const sectionIndices = [];
    cleaned.forEach((line, index) => {
        const hdr = isSectionHeader(line);
        if (hdr) {
            const last = sectionIndices[sectionIndices.length - 1];
            if (last && last.key === hdr.key) return;
            sectionIndices.push({ index, ...hdr });
        }
    });

    const firstSectionIndex = sectionIndices[0]?.index ?? cleaned.length;
    const header = parseHeaderLines(cleaned, firstSectionIndex);
    const sectionTitles = { ...DEFAULT_ATS_SECTION_TITLES };
    const result = {
        ...header,
        summary: "",
        skills: "",
        certifications: "",
        languages: "",
        experiences: [],
        education: [],
        projects: [],
        customSections: [],
        sectionTitles,
        warnings,
    };

    for (let s = 0; s < sectionIndices.length; s++) {
        const { index, key, title } = sectionIndices[s];
        const end = sectionIndices[s + 1]?.index ?? cleaned.length;
        const body = cleaned.slice(index + 1, end);

        if (key === "custom" || !KNOWN_SECTION_KEYS.has(key)) {
            if (body.length) {
                result.customSections.push({
                    ...createCustomSection(`import-custom-${s}`),
                    id: `import-custom-${s}`,
                    title: title || "ADDITIONAL SECTION",
                    layout: "bullets",
                    content: listSectionToText(body),
                });
            }
            continue;
        }

        if (title) sectionTitles[key] = title;

        switch (key) {
            case "summary":
                result.summary = listSectionToText(body) || body.join(" ").trim();
                break;
            case "experience": {
                const exps = parseEntrySection(body, createExperienceEntry, "experience");
                if (exps.length) result.experiences = reindexEntries(exps, "imp-exp", createExperienceEntry);
                break;
            }
            case "education": {
                const edus = parseEntrySection(body, createEducationEntry, "education");
                if (edus.length) result.education = reindexEntries(edus, "imp-edu", createEducationEntry);
                break;
            }
            case "projects": {
                const projs = parseEntrySection(body, createProjectEntry, "project");
                if (projs.length) result.projects = reindexEntries(projs, "imp-proj", createProjectEntry);
                break;
            }
            case "skills":
                result.skills = listSectionToText(body);
                break;
            case "certifications":
                result.certifications = listSectionToText(body);
                break;
            case "languages":
                result.languages = listSectionToText(body);
                break;
            default:
                break;
        }
    }

    if (!sectionIndices.length) {
        warnings.push(
            "সেকশন শিরোনাম চেনা যায়নি। হেডার ইম্পোর্ট হয়েছে — বাকি অংশ ম্যানুয়ালি পূরণ করুন।"
        );
    } else if (sectionIndices.length < 3) {
        warnings.push("কিছু সেকশন মিস হতে পারে — ইম্পোর্ট পর তথ্য যাচাই করুন।");
    }

    if (!displayField(result.fullName) && cleaned[0]) {
        result.fullName = cleaned[0];
    }

    if (!result.experiences.length) {
        result.experiences = [createExperienceEntry("exp-import")];
    }
    if (!result.education.length) {
        result.education = [createEducationEntry("edu-import")];
    }

    result.sectionTitles = sectionTitles;
    return result;
}

export function parseAtsCvFromText(text) {
    const lines = String(text ?? "")
        .split(/\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    return parseAtsCvFromLines(lines);
}

export function mergeImportedAtsCvForm(imported) {
    const experiences =
        imported.experiences?.length > 0
            ? reindexEntries(imported.experiences, "imp-exp", createExperienceEntry)
            : [createExperienceEntry("exp-1")];

    const education =
        imported.education?.length > 0
            ? reindexEntries(imported.education, "imp-edu", createEducationEntry)
            : [createEducationEntry("edu-1")];

    const projects =
        imported.projects?.length > 0
            ? reindexEntries(imported.projects, "imp-proj", createProjectEntry)
            : [];

    const customSections = (imported.customSections || []).map((sec, i) => ({
        ...createCustomSection(`import-custom-${i}`),
        ...sec,
        id: sec.id || `import-custom-${i}`,
    }));

    return {
        fullName: displayField(imported.fullName) || "",
        jobTitle: displayField(imported.jobTitle) || "",
        email: displayField(imported.email) || "",
        phone: displayField(imported.phone) || "",
        location: displayField(imported.location) || "",
        linkedin: displayField(imported.linkedin) || "",
        website: displayField(imported.website) || "",
        summary: displayField(imported.summary) || "",
        skills: typeof imported.skills === "string" ? imported.skills : "",
        certifications:
            typeof imported.certifications === "string" ? imported.certifications : "",
        languages: typeof imported.languages === "string" ? imported.languages : "",
        experiences,
        education,
        projects,
        sectionTitles: {
            ...DEFAULT_ATS_SECTION_TITLES,
            ...(imported.sectionTitles || {}),
        },
        customSections,
    };
}
